import type { RoleViewExtension as RoleViewModel } from 'tio-data/data/resources/role-view.ext';
import type { UserRole as UserRoleModel } from 'tio-data/data/resources/user-role.type';
import type { UserRelationshipExtension as UserRelationshipModel } from 'tio-data/data/resources/user-relationship.ext';
import type { User } from './user.type.ts';
export interface UserExtension extends User {}
export class UserExtension {
  // ── Computed Properties ──
  /**
   * Derives role views from userRoles relationships, falling back to roleViews. Company comes from
   * jurisdiction.company, not the legacy organization user at jurisdiction.user.
   */
  get computedRoleViews(): RoleViewModel[] {
    if (!this.userRoles || this.userRoles.length === 0) {
      return this.roleViews || [];
    }

    const computed: Partial<RoleViewModel>[] = [];
    this.userRoles.forEach((userRole: UserRoleModel) => {
      // is_active mirrors discarded_at here, and a discarded role's relationships stay active.
      if (!userRole.isActive) {
        return;
      }

      if (!userRole.relationships || userRole.relationships.length === 0) {
        // If role has no relationships, still create a role view entry
        // For EMPLOYEE roles without relationships, try to get company from employee
        const company = userRole.employee?.company ?? undefined;
        computed.push({
          id: userRole.id,
          role: userRole.roleType,
          roleType: userRole.roleType,
          relationshipType: userRole.roleType,
          isActive: userRole.isActive,
          company,
        } as Partial<RoleViewModel>);
      } else {
        userRole.relationships.forEach((relationship: UserRelationshipModel) => {
          // Get company from jurisdiction's company relationship (Company.user_role_id = jurisdiction.id)
          // This is the actual Company record, not the legacy ORGANIZATION User
          const jurisdictionCompany = relationship.jurisdiction?.company;
          computed.push({
            id: relationship.id,
            role: userRole.roleType,
            roleType: userRole.roleType,
            relationshipType: relationship.relationshipType,
            isActive: relationship.isActive,
            company: jurisdictionCompany ?? undefined,
          } as Partial<RoleViewModel>);
        });
      }
    });

    return computed as RoleViewModel[];
  }

  get roles() {
    return this.computedRoleViews;
  }

  get primaryEmail() {
    return this.emailAddresses?.find((e) => e.contactType === 'PRIMARY');
  }

  get personalEmail() {
    return this.emailAddresses?.find((e) => e.contactType === 'PERSONAL');
  }
  get uniqueRolesByRelationshipType() {
    const relationshipMap = new Map();
    this.roles.forEach((role) => {
      const existingRole = relationshipMap.get(role.relationshipType);
      if (!existingRole) {
        // If no role exists for this relationshipType, add it
        relationshipMap.set(role.relationshipType, role);
      } else if (!existingRole.isActive && role.isActive) {
        // If a duplicate exists, prefer the active role
        relationshipMap.set(role.relationshipType, role);
      }
      // If both are active or both are inactive, keep the first one (no action needed)
    });

    const uniqueRoles = Array.from(relationshipMap.values());
    return Object.freeze(uniqueRoles);
  }

  get needsToAnswerWhyNoLinkedLoans() {
    return !!this.person?.needsToAnswerWhyNoLinkedLoans;
  }

  get isTioAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TIO_ADMIN';
    });
  }

  /**
   * Mirrors ReportPolicy#view_internal_reports? for navigation. The API independently restricts
   * internal attributes and filtering; this getter does not authorize data access.
   */
  get isInternalReportViewer() {
    const INTERNAL_REPORT_VIEWER_ROLES = ['TIO_DEVOPS', 'ACCOUNT_MANAGER', 'ACCOUNT_MANAGER_MGR'];

    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && INTERNAL_REPORT_VIEWER_ROLES.includes(jr.relationshipType)
        )
      );
    });
  }

  get isTioDevOps() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'TIO_DEVOPS'
        )
      );
    });
  }

  // TA_AUDITOR audits TA AI decisions and TA_ESCALATION_REVIEWER resolves
  // escalated audits. Both are jurisdictive relationship types on the user's
  // active TIO employee, like TIO_DEVOPS.
  get isTaAuditor() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'TA_AUDITOR'
        )
      );
    });
  }

  // Audits TA approver (human) decisions; isTaAuditor covers the AI's. Held
  // together they confer both, which is why they are separate getters.
  get isTaApproverAuditor() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'TA_APPROVER_AUDITOR'
        )
      );
    });
  }

  get isTaEscalationReviewer() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'TA_ESCALATION_REVIEWER'
        )
      );
    });
  }

  // Single gate for the in-product admin assistant in tio-admin. Mirrors
  // TokenUser#mcp_chat_authorized? on tio-api — add roles here to widen
  // access without touching individual components.
  get canAccessAdminChat() {
    return this.isTioAdmin || this.isTioDevOps;
  }

  get isCustomerServiceManager() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'CUSTOMER_SERVICE_MGR'
        )
      );
    });
  }

  get isTioPartnerContractManager() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'TIO_PARTNER_CONTRACT_MANAGER'
        )
      );
    });
  }

  get isEmployee() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'PARTICIPANT';
    });
  }

  get isEmployeeAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'EMPLOYEE_ADMIN';
    });
  }

  get isAccountOwner() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'ACCOUNT_OWNER';
    });
  }

  get isAccountOwnerOrEmployeeAdmin() {
    return this.isAccountOwner || this.isEmployeeAdmin;
  }

  get isAgent() {
    return !!this.roles?.find((role) => {
      return role.role === 'WORKSPACE_AGENT';
    });
  }

  get isFamily() {
    return !!this.roles?.find((role) => {
      return role.role === 'FAMILY_MEMBER';
    });
  }

  get isPslfAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'PSLF_ADMIN';
    });
  }

  get isTasTioApprover() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TAS.Approver.TIO_APPROVER';
    });
  }

  get isTasAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType.startsWith('TAS.Admin');
    });
  }

  get isTasProgramAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TAS.Admin.PROGRAM_ADMIN';
    });
  }

  get isTasApprover() {
    return !!this.roles?.find((role) => {
      return role.relationshipType.startsWith('TAS.Approver');
    });
  }

  get isTasSupervisor() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TAS.Approver.SUPERVISOR';
    });
  }

  get isPayOps() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TIO_PAYMENT_OPS';
    });
  }

  get canEditTasApplication() {
    const approvedRoles = [
      'TAS.Admin.PROGRAM_ADMIN',
      'TAS.Admin.SUPERUSER',
      'TAS.Approver.ADMIN_APPROVER',
      'TAS.Approver.APPROVER',
      'TAS.Approver.CUSTOM_APPROVER',
      'TAS.Approver.FINANCE_APPROVER',
      'TAS.Approver.HYPERVISOR',
      'TAS.Approver.PPP_FINANCE_APPROVER',
      'TAS.Approver.TAP_FINANCE_APPROVER',
      'TAS.Approver.TIO_APPROVER',
      'TAS.Approver.SUPERVISOR',
      'TIO_ACCOUNT_MANAGER',
      'TIO_ADMIN',
      'EMPLOYEE_ADMIN',
      'TIO_PAYMENT_OPS',
    ];
    return !!this.roles?.find((role) => {
      return approvedRoles.includes(role.relationshipType);
    });
  }

  get isEnrolled() {
    return !!this.logins?.find((login) => login.isActive);
  }
}

const Registration = {
  kind: 'object',
  name: 'user',
  features: UserExtension,
};
export default Registration;
